import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, brl, productImage, uploadUrl, API_BASE } from "../lib/api";
import { useCart } from "../lib/store";
import { toast } from "sonner";
import { Upload, Minus, Plus, Check, ArrowLeft, Sparkles, Play, Volume2, VolumeX } from "lucide-react";

function VideoPlayer({ videoId, poster }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="relative rounded-3xl overflow-hidden bg-[#0D1B2A] aspect-video">
      {!loaded && poster && <img src={poster} alt="preview" className="absolute inset-0 w-full h-full object-cover" />}
      <video
        ref={ref}
        data-testid={`product-video-${videoId}`}
        src={`${API_BASE}/videos/${videoId}`}
        className="w-full h-full object-cover"
        playsInline
        muted={muted}
        preload="metadata"
        onLoadedData={() => setLoaded(true)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {!playing && (
          <button onClick={() => ref.current?.play()} className="pointer-events-auto w-16 h-16 rounded-full bg-[#D4AF37] text-[#0D1B2A] flex items-center justify-center shadow-2xl">
            <Play size={26} fill="currentColor" />
          </button>
        )}
      </div>
      <button
        onClick={() => { const v = ref.current; if (!v) return; v.muted = !v.muted; setMuted(v.muted); }}
        className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center backdrop-blur"
        aria-label="mute"
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
    </div>
  );
}

function StoryCards({ cards }) {
  const [active, setActive] = useState(0);
  if (!cards?.length) return null;
  const c = cards[active];
  return (
    <div>
      <div className="eyebrow">Como funciona</div>
      <h3 className="mt-2 font-display text-3xl text-[#0D1B2A]">Descubra cada detalhe</h3>
      <div className="mt-6 grid md:grid-cols-2 gap-6 items-start">
        <motion.div key={active} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}
          className="rounded-3xl overflow-hidden aspect-[4/3] bg-[#F7F2E7]"
        >
          {c.image ? <img src={c.image} alt={c.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-[#0D1B2A]/40 font-display text-2xl">Eterniza</div>}
        </motion.div>
        <div>
          {cards.map((s, i) => (
            <button
              key={i}
              data-testid={`story-card-${i}`}
              onClick={() => setActive(i)}
              className={`w-full text-left p-4 rounded-2xl border transition-all ${
                active === i
                  ? "border-[#D4AF37] bg-[#F7F2E7]"
                  : "border-[#0D1B2A]/10 bg-white hover:border-[#D4AF37]/40"
              } ${i > 0 ? "mt-3" : ""}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-[#0D1B2A] text-[#D4AF37] flex items-center justify-center font-display text-sm">0{i + 1}</span>
                <div className="font-display text-lg text-[#0D1B2A]">{s.title}</div>
              </div>
              {active === i && s.description && <p className="text-sm text-[#3A4A5F] mt-2 pl-11">{s.description}</p>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [photoId, setPhotoId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState("");
  const fileRef = useRef(null);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => setP(data)).catch(() => setP(false));
  }, [slug]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Arquivo maior que 15MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("owner_email", "guest");
    try {
      const r = await fetch(`${API_BASE}/uploads`, { method: "POST", body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "erro");
      setPhotoId(data.id);
      toast.success("Foto enviada!");
    } catch (err) { toast.error("Falha no upload"); }
    finally { setUploading(false); }
  };

  const addToCart = () => {
    add({
      product_id: p.id, name: p.name, price: p.price, image_url: p.image_url,
      photo_id: photoId, customization: notes ? { notes } : {}, quantity: qty,
    });
    toast.success("Adicionado ao carrinho");
  };

  if (p === null) return <div className="mx-auto max-w-7xl px-6 py-24 text-center text-[#6B7280]">Carregando…</div>;
  if (p === false) return <div className="mx-auto max-w-7xl px-6 py-24 text-center">Produto não encontrado. <Link to="/catalogo" className="text-[#D4AF37]">Voltar</Link></div>;

  const videos = p.video_ids || [];

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-10" data-testid="product-detail-root">
      <Link to="/catalogo" className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#0D1B2A]">
        <ArrowLeft size={14} /> Voltar
      </Link>

      <div className="mt-6 grid lg:grid-cols-2 gap-12 items-start">
        <div className="relative">
          <div className="rounded-3xl overflow-hidden bg-[#F7F2E7] aspect-[4/5]">
            <img src={productImage(p)} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {photoId && (
            <div className="absolute -bottom-6 -right-6 w-32 aspect-square rounded-2xl overflow-hidden border-8 border-white shadow-xl rotate-3">
              <img src={uploadUrl(photoId)} alt="sua foto" className="w-full h-full object-cover" />
            </div>
          )}
        </div>

        <div>
          <div className="eyebrow">{p.category}</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl text-[#0D1B2A] leading-tight">{p.name}</h1>
          {p.tagline && <div className="mt-2 font-script text-[#D4AF37] text-2xl">{p.tagline}</div>}
          <div className="mt-3 font-display text-3xl text-[#0D1B2A]">{brl(p.price)}</div>
          <p className="mt-6 text-[#3A4A5F] leading-relaxed">{p.description}</p>

          <div className="gold-rule my-8" />

          {p.allow_photo_upload && (
            <div className="mb-6">
              <div className="eyebrow">Personalize com sua foto</div>
              <div onClick={() => fileRef.current?.click()}
                className="mt-3 border-2 border-dashed border-[#D4AF37]/60 bg-[#F7F2E7]/60 rounded-2xl p-6 flex items-center gap-4 cursor-pointer hover:bg-[#F7F2E7]"
                data-testid="upload-zone"
              >
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center border border-[#D4AF37]/40">
                  {photoId ? <Check size={22} className="text-[#0D1B2A]" /> : <Upload size={20} className="text-[#0D1B2A]" />}
                </div>
                <div>
                  <div className="font-display text-[#0D1B2A]">
                    {uploading ? "Enviando…" : photoId ? "Foto enviada!" : "Enviar foto para o produto"}
                  </div>
                  <div className="text-xs text-[#6B7280]">JPG ou PNG · até 15MB</div>
                </div>
                <input ref={fileRef} data-testid="upload-input" type="file" accept="image/*" className="hidden" onChange={handleFile} />
              </div>
            </div>
          )}

          <div className="mb-6">
            <label className="eyebrow">Observações</label>
            <textarea data-testid="product-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="input-luxe mt-2" rows={3}
              placeholder="Ex.: nome dos filhos, data, cores..." />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 border border-[#0D1B2A]/10 rounded-full px-2 bg-white">
              <button data-testid="qty-dec" className="w-9 h-9 flex items-center justify-center" onClick={() => setQty((q) => Math.max(1, q - 1))}><Minus size={14} /></button>
              <span data-testid="qty-val" className="w-8 text-center font-medium">{qty}</span>
              <button data-testid="qty-inc" className="w-9 h-9 flex items-center justify-center" onClick={() => setQty((q) => q + 1)}><Plus size={14} /></button>
            </div>
            <button data-testid="add-to-cart" onClick={addToCart} className="btn-gold flex-1 inline-flex items-center justify-center gap-2">
              <Sparkles size={16} /> Adicionar {brl(p.price * qty)}
            </button>
          </div>
        </div>
      </div>

      {videos.length > 0 && (
        <section className="mt-16">
          <div className="eyebrow">Assista em movimento</div>
          <h2 className="mt-2 font-display text-3xl text-[#0D1B2A]">Veja como fica</h2>
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {videos.map((vid) => <VideoPlayer key={vid} videoId={vid} poster={p.image_url} />)}
          </div>
        </section>
      )}

      {p.story_cards?.length > 0 && (
        <section className="mt-16">
          <StoryCards cards={p.story_cards} />
        </section>
      )}
    </div>
  );
}
